import { useEffect, useMemo, useState } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'

import type { Tenant } from '../components/flowDebugger/types'
import { traceService } from '../services/traceService'
import DebugObservabilityOverview from './DebugObservabilityOverview'
import DebugObservabilityAlerts from './DebugObservabilityAlerts'
import DebugObservabilityFunnel from './DebugObservabilityFunnel'
import DebugObservabilityConversations from './DebugObservabilityConversations'

function tabClassName(isActive: boolean) {
  return [
    'obs-tab',
    isActive ? 'active' : '',
  ].filter(Boolean).join(' ')
}

export default function DebugObservabilityLayout() {
  const location = useLocation()
  const [tenants, setTenants] = useState<Tenant[]>([])
  const [tenantId, setTenantId] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setLoading(true)
      setError('')
      try {
        const res = await traceService.getTenants()
        if (cancelled) return
        const list = res.data || []
        setTenants(list)
        setTenantId((current) => current || list[0]?.id || '')
      } catch {
        if (!cancelled) setError('No se pudieron cargar los tenants')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [])

  const tenant = useMemo(() => tenants.find((t) => t.id === tenantId) || null, [tenants, tenantId])
  const pathname = location.pathname
  const activeSection: 'funnel' | 'alerts' | 'conversations' | 'overview' | 'simulator' = pathname.startsWith('/admin/debug/funnel') || pathname.endsWith('/funnel')
    ? 'funnel'
    : pathname.startsWith('/admin/debug/alerts') || pathname.endsWith('/alerts')
      ? 'alerts'
      : pathname.startsWith('/admin/debug/conversations') || pathname.endsWith('/conversations')
        ? 'conversations'
        : pathname.startsWith('/admin/debug/simulator')
          ? 'simulator'
          : 'overview'

  const [windowMinutes, setWindowMinutes] = useState(1440)
  const [activeInvariantWarnCount, setActiveInvariantWarnCount] = useState(0)

  // Badge del tab "Alertas": cuenta de alertas activas de invariantes en modo warn
  // (M-27). Lectura barata respaldada por Redis; superficie de debug únicamente.
  useEffect(() => {
    if (!tenantId) {
      setActiveInvariantWarnCount(0)
      return
    }
    let cancelled = false
    const load = async () => {
      try {
        const res = await traceService.getObservabilityAlerts({
          tenantId,
          status: 'active',
          minutes: windowMinutes,
        })
        if (cancelled) return
        const count = (res.data || []).filter((alert) => alert.type === 'invariant_warn').length
        setActiveInvariantWarnCount(count)
      } catch {
        if (!cancelled) setActiveInvariantWarnCount(0)
      }
    }
    void load()
    const interval = window.setInterval(load, 60_000)
    return () => {
      cancelled = true
      window.clearInterval(interval)
    }
  }, [tenantId, windowMinutes])

  return (
    <div className="obs-root">
      <div className="obs-topbar">
        <div className="obs-topbar-left">
          <div className="obs-title">Agent Observability Console</div>
          <div className="obs-subtitle">LangGraph Sales Agent</div>
        </div>

        <div className="obs-topbar-right">
          <Link className="obs-link" to="/admin" style={{ color: '#60a5fa', fontWeight: 700 }}>
            ← Volver a Admin
          </Link>
          <Link className="obs-link" to="/admin/debug">
            Volver a Trace Inspector
          </Link>
          <Link className={`obs-link ${activeSection === 'simulator' ? 'active' : ''}`} to={`/admin/debug/simulator${tenantId ? `?tenantId=${tenantId}` : ''}`}>
            Simulador
          </Link>
          <Link className={`obs-link ${activeSection === 'overview' ? 'active' : ''}`} to="/admin/debug/observability">
            Overview
          </Link>
          <Link className={`obs-link ${activeSection === 'alerts' ? 'active' : ''}`} to="/admin/debug/alerts">
            Alerts
            {activeInvariantWarnCount > 0 ? (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: 18,
                  height: 18,
                  borderRadius: 999,
                  background: 'rgba(245,158,11,0.9)',
                  color: '#1c1917',
                  fontSize: 10,
                  fontWeight: 900,
                  marginLeft: 6,
                  padding: '0 5px',
                }}
                aria-label={`${activeInvariantWarnCount} alertas activas de invariantes en modo warn`}
              >
                {activeInvariantWarnCount}
              </span>
            ) : null}
          </Link>
          <Link className={`obs-link ${activeSection === 'funnel' ? 'active' : ''}`} to="/admin/debug/funnel">
            Funnel
          </Link>
          <Link className={`obs-link ${activeSection === 'conversations' ? 'active' : ''}`} to="/admin/debug/conversations">
            Conversations
          </Link>
        </div>
      </div>

      <div className="obs-controls">
        <div className="obs-control">
          <div className="obs-label">Tenant</div>
          {loading ? (
            <div className="obs-muted">Cargando...</div>
          ) : error ? (
            <div className="obs-error">{error}</div>
          ) : (
            <select
              className="obs-select"
              value={tenantId}
              onChange={(e) => setTenantId(e.target.value)}
            >
              {tenants.map((t) => (
                <option key={t.id} value={t.id}>{t.name || t.slug || t.id}</option>
              ))}
            </select>
          )}
        </div>

        <div className="obs-control" style={{ maxWidth: 220 }}>
          <div className="obs-label">Ventana Temporal</div>
          <select
            className="obs-select"
            value={windowMinutes}
            onChange={(e) => setWindowMinutes(Number(e.target.value))}
          >
            <option value={10}>Últimos 10 minutos</option>
            <option value={60}>Última 1 hora</option>
            <option value={360}>Últimas 6 horas</option>
            <option value={1440}>Últimas 24 horas</option>
            <option value={10080}>Últimos 7 días</option>
            <option value={43200}>Últimos 30 días</option>
          </select>
        </div>

        <div className="obs-meta">
          <div className="obs-meta-row">
            <span className="obs-k">tenantId</span>
            <span className="obs-v">{tenantId || '-'}</span>
          </div>
          <div className="obs-meta-row">
            <span className="obs-k">slug</span>
            <span className="obs-v">{tenant?.slug || '-'}</span>
          </div>
        </div>
      </div>

      <div className="obs-tabs">
        <NavLink to="/admin/debug/observability" end className={({ isActive }) => tabClassName(isActive)}>
          Overview
        </NavLink>
        <NavLink to="/admin/debug/alerts" className={({ isActive }) => tabClassName(isActive)}>
          Alertas
        </NavLink>
        <NavLink to="/admin/debug/funnel" className={({ isActive }) => tabClassName(isActive)}>
          Funnel
        </NavLink>
        <NavLink to="/admin/debug/conversations" className={({ isActive }) => tabClassName(isActive)}>
          Conversaciones Largas
        </NavLink>
      </div>

      <div className="obs-content">
        {activeSection === 'alerts' ? (
          <DebugObservabilityAlerts tenantId={tenantId} minutes={windowMinutes} />
        ) : activeSection === 'funnel' ? (
          <DebugObservabilityFunnel tenantId={tenantId} minutes={windowMinutes} />
        ) : activeSection === 'conversations' ? (
          <DebugObservabilityConversations tenantId={tenantId} />
        ) : (
          <DebugObservabilityOverview tenantId={tenantId} minutes={windowMinutes} />
        )}
      </div>

      <style>{`
        .obs-root {
          min-height: 100vh;
          color: #e2e8f0;
          background:
            radial-gradient(1200px 700px at 20% -10%, rgba(34,197,94,0.22), transparent 60%),
            radial-gradient(900px 600px at 95% 10%, rgba(59,130,246,0.20), transparent 55%),
            linear-gradient(180deg, #070a0d 0%, #05070a 100%);
        }
        .obs-topbar {
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          padding: 18px 22px;
          border-bottom: 1px solid rgba(255,255,255,0.08);
          background: rgba(255,255,255,0.02);
          position: sticky;
          top: 0;
          z-index: 3;
          backdrop-filter: blur(8px);
        }
        .obs-topbar-right {
          display: flex;
          flex-wrap: wrap;
          justify-content: flex-end;
          gap: 8px;
        }
        .obs-title {
          font-size: 16px;
          font-weight: 800;
          letter-spacing: 0.02em;
        }
        .obs-subtitle {
          font-size: 12px;
          color: rgba(226,232,240,0.65);
          margin-top: 2px;
        }
        .obs-link {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          color: #93c5fd;
          text-decoration: none;
          padding: 8px 10px;
          border-radius: 10px;
          border: 1px solid rgba(59,130,246,0.26);
          background: rgba(59,130,246,0.10);
        }
        .obs-link:hover { filter: brightness(1.08); }
        .obs-link.active {
          color: #052e16;
          border-color: rgba(34,197,94,0.45);
          background: rgba(34,197,94,0.92);
        }

        .obs-controls {
          display: flex;
          gap: 14px;
          align-items: stretch;
          padding: 14px 22px;
          border-bottom: 1px solid rgba(255,255,255,0.06);
        }
        .obs-control {
          flex: 1;
          min-width: 260px;
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.09);
          border-radius: 14px;
          padding: 12px 12px;
        }
        .obs-label {
          font-size: 10px;
          text-transform: uppercase;
          letter-spacing: 0.09em;
          color: rgba(226,232,240,0.50);
          margin-bottom: 8px;
        }
        .obs-select {
          width: 100%;
          padding: 10px 10px;
          border-radius: 10px;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.14);
          color: #e2e8f0;
          font-size: 13px;
        }
        .obs-muted { color: rgba(226,232,240,0.5); font-size: 13px; }
        .obs-error { color: #fca5a5; font-size: 13px; }

        .obs-meta {
          width: 320px;
          background: rgba(255,255,255,0.02);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 14px;
          padding: 12px 14px;
        }
        .obs-meta-row { display: flex; justify-content: space-between; gap: 12px; padding: 4px 0; }
        .obs-k { font-size: 11px; color: rgba(226,232,240,0.55); font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace; }
        .obs-v { font-size: 12px; color: rgba(226,232,240,0.92); font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace; word-break: break-all; text-align: right; }

        .obs-tabs {
          display: flex;
          gap: 8px;
          padding: 12px 22px;
          border-bottom: 1px solid rgba(255,255,255,0.06);
        }
        .obs-tab {
          font-size: 12px;
          text-decoration: none;
          color: rgba(226,232,240,0.7);
          padding: 8px 12px;
          border-radius: 999px;
          border: 1px solid rgba(255,255,255,0.10);
          background: rgba(255,255,255,0.02);
        }
        .obs-tab.active {
          color: #052e16;
          border-color: rgba(34,197,94,0.45);
          background: rgba(34,197,94,0.92);
          box-shadow: 0 12px 32px rgba(34,197,94,0.14);
        }

        .obs-content {
          padding: 18px 22px 30px 22px;
        }

        @media (max-width: 900px) {
          .obs-controls { flex-direction: column; }
          .obs-meta { width: auto; }
        }
      `}</style>
    </div>
  )
}
